import { Component, EventEmitter, Input, OnInit, Output, AfterViewInit, OnDestroy, ElementRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, finalize, map, Observable, of, Subject, takeUntil, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { BaseSearchOptions } from '../../../models/core/table/BaseSearchOptions';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { LoadingService } from '../../../services/loading.service';
import { ObjectUtility } from '../../../utilities/ObjectUtility';
import { whitespaceValidator } from '../../../validators/ValidationService';
import { Die } from '../../../models/domain/Die';
import { DieService } from '../../../services/die.service';
import { Category } from '../../../models/domain/Category';
import { Tenant } from '../../../models/domain/Tenant';
import { CategoryService } from '../../../services/category.service';
import { TenantService } from '../../../services/tenant.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { DieDetails } from '../../../models/domain/DieDetails';
import { environment } from '../../../../environments/environment';
import { PdfToImageComponent } from '../pdf-to-image/pdf-to-image.component';
import { optionalDecimalValidator } from '../../../validators/optionalDecimalValidator';
import { weightFormatValidator } from '../../../validators/weightFormatValidator';
import { photoValidator } from '../../../validators/photoValidator';
import { CapitalizeWordsDirective } from '../../../directive/CapitalizeWordsDirective';
import { UrlHelper } from '../../../utilities/UrlHelper';

@Component({
  selector: 'app-die-details',
  templateUrl: './die-details.component.html',
  styleUrl: './die-details.component.scss',
  imports: [ReactiveFormsModule, CommonModule, PdfToImageComponent, CapitalizeWordsDirective],
})

export class DieDetailsComponent implements OnInit, AfterViewInit, OnDestroy {

  @Output() classApplied = new EventEmitter<boolean>();
  @Output() saveConfirmationEvent = new EventEmitter<boolean>();
  @Input() currentDieId = 0;
  // isToggled
  isToggled = false;
  frmGroup!: FormGroup;

  @ViewChild('fileInput') fileInput!: ElementRef;

  public dieDetails: DieDetails[] = [];

  private destroy$: Subject<boolean> = new Subject<boolean>();
  public dieOriginal = new Die;
  public die: Die = new Die;
  public titleText = "";
  public titleButton = "";
  public errorMessage = "";
  isSubmitted = false;

  //#region Dropdown DataBind
  public category$!: Observable<Category[]>;
  public category!: Category[];

  public tenant$!: Observable<Tenant[]>;
  public tenant!: Tenant[];

  public pageIndex = 0;
  public pageSize = 1000;
  public searchOptions!: BaseSearchOptions;
  // #endregion

  classAppliedPdfToImage = false;
  isNewCategory = false;

  constructor(
    public themeService: CustomizerSettingsService,
    private formBuilder: FormBuilder,
    private dieService: DieService,
    private toasterService: ToastrService,
    private loadingService: LoadingService,
    private categoryService: CategoryService,
    private tenantService: TenantService,
    private authService: AuthenticationService,
    private cdr: ChangeDetectorRef


  ) {
    this.initialize();
  }

  public ngOnInit(): void {
    if (this.currentDieId > 0) {
      this.getDropdownData();
    }
    else {
      this.currentDieId = 0;
      this.getDropdownData();
      const newDie = new Die();
      this.die = ObjectUtility.deepClone(newDie);
      this.dieOriginal = ObjectUtility.deepClone(newDie);

      // Automatically select the default tenant and load related categories
      const currentUser = this.authService.getCurrentUser();
      if (currentUser) {
        const loggedInTenantId = currentUser.tenantId;
        this.selectDefaultTenant(loggedInTenantId);
      }
      this.setForm(this.die);
    }
  }

  private initialize(): void {
    this.themeService.isToggled$.subscribe(isToggled => {
      this.isToggled = isToggled;
    });

    this.searchOptions = new BaseSearchOptions(undefined, 'name', 0, this.pageIndex, this.pageSize);
    this.isSubmitted = false;
    this.createForm();
  }

  public ngAfterViewInit(): void {
    this.frmGroup.controls['noControl'].valueChanges.subscribe(value => {
      this.die.no = value;
    });

    this.frmGroup.controls['nameControl'].valueChanges.subscribe(value => {
      this.die.name = value;
    });

    this.frmGroup.controls['photoUrlControl'].valueChanges.subscribe(value => {
      this.die.photoUrl = value;
    });

    this.frmGroup.controls['categoryControl'].valueChanges.subscribe(value => {
      this.die.category = value;
    });

    this.frmGroup.controls['tenantControl'].valueChanges.subscribe(value => {
      this.die.tenant = value;
    });

    this.dieDetailsArray.valueChanges.subscribe((values) => {
      // Update the dieDetails property with latest form array values
      this.die.dieDetails = values;
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  //#region Form Controls Getters Setter  
  get f() { return this.frmGroup.controls; }

  get dieDetailsArray(): FormArray {
    return this.frmGroup.get('dieDetails') as FormArray;
  }

  private createForm(): void {
    this.frmGroup = this.formBuilder.group({
      noControl: new FormControl('', [Validators.required, whitespaceValidator()]),
      nameControl: new FormControl(null, [whitespaceValidator()]),
      photoUrlControl: new FormControl(null, [Validators.required, photoValidator(), whitespaceValidator()]),
      categoryControl: new FormControl(null, [Validators.required]),
      newCategoryControl: new FormControl(null, whitespaceValidator()),
      tenantControl: new FormControl(null, [Validators.required]),
      dieDetails: this.formBuilder.array([]) // <- FormArray inside main form
    })
  }

  private createDetailGroup(detail?: DieDetails): FormGroup {
    return this.formBuilder.group({
      id: [detail?.id ?? 0],
      sectionNo: [detail?.sectionNo ?? '', [Validators.required, whitespaceValidator()]],
      a: [detail?.a ?? null, [Validators.required, Validators.pattern(/^\d+(\.\d+)?$/)]],
      b: [detail?.b ?? null, [Validators.required, Validators.pattern(/^\d+(\.\d+)?$/)]],
      t: [detail?.t ?? null, optionalDecimalValidator()],
      weight: [detail?.weight ?? '', [Validators.required, weightFormatValidator()]]
    });
  }

  addDetailRow(): void {
    this.dieDetailsArray.push(this.createDetailGroup());
  }

  removeDetailRow(index: number): void {
    this.dieDetailsArray.removeAt(index);
  }

  private setForm(die: Die): void {
    if (die) {
      this.frmGroup.patchValue({
        noControl: die.no,
        nameControl: die.name,
        photoUrlControl: die.photoUrl, // this holds the image path, not base64
        categoryControl: die.category,
        tenantControl: die.tenant,
      });

      // Reset base64 since no new photo selected yet
      this.die.photoBase64 = '';

      // Clear existing dieDetails FormArray controls before adding new ones
      this.dieDetailsArray.clear();

      if (die.dieDetails && die.dieDetails.length > 0) {
        die.dieDetails.forEach(detail => {
          this.dieDetailsArray.push(this.createDetailGroup(detail));
        });
      }

    }
    this.titleText = `${this.currentDieId === 0 ? "Add" : "Edit"} Die`;
    this.titleButton = `${this.currentDieId === 0 ? "Create" : "Update"}`;
  }

  //#endregion

  public onSave(): void {
    this.isSubmitted = true;
    this.frmGroup.controls["dieDetails"].markAllAsTouched(); // Mark all controls as touched to show validation errors

    if (this.frmGroup.valid) {
      this.loadingService.showLoading();
      this.addEditDie();
    }
  }

  private addEditDie(): void {
    if (this.die?.id === 0) {
      this.dieService.create(this.sanitizeService(this.die))
        .pipe(
          catchError((err: any) => {
            if (err && err.error) {
              this.isSubmitted = false;
              if (err.error.isCustom) {
                this.errorMessage = err.error.message;
                this.toasterService.warning(err.error.message);
              } else {
                this.toasterService.error('Something went wrong!');
              }
            } else {
              this.isSubmitted = false;
              this.toasterService.error('Something went wrong!');
            }
            this.loadingService.hideLoading();

            return of(null);
          }),
          finalize(() => {
            this.loadingService.hideLoading();
          }),
          takeUntil(this.destroy$))
        .subscribe(response => {
          if (response) {
            this.isSubmitted = false;
            this.die = response?.body != null ? response?.body : new Die;
            this.saveConfirmationEvent.emit(true);
            this.toggleClass();
            this.toasterService.success("Save successfully");
          }
          this.loadingService.hideLoading();
        });
    } else {
      this.dieService.update(this.sanitizeService(this.die))
        .pipe(
          catchError((err: any) => {
            if (err && err.error) {
              this.isSubmitted = false;
              if (err.error.isCustom) {
                this.errorMessage = err.error.message;
                this.toasterService.warning(err.error.message);
              } else {
                this.toasterService.error('Something went wrong!');
              }
            } else {
              this.isSubmitted = false;
              this.toasterService.error('Something went wrong!');
            }
            return of(null);
          }),
          takeUntil(this.destroy$),
          // Ensure loading is always hidden, even if error is not caught
          finalize(() => {
            this.loadingService.hideLoading();
          })
        )
        .subscribe(response => {
          if (response) {
            this.isSubmitted = false;
            this.die = response?.body != null ? response?.body : new Die;
            this.saveConfirmationEvent.emit(true);
            this.toasterService.success("Update successfully");
            this.toggleClass();
          }
        });
    }
  }


  public toggleClass(): void {
    this.classApplied.emit(false);
  }

  public dataHasChanged(): boolean {
    const baseEqual = ObjectUtility.deepEqual(this.die, this.dieOriginal);
    return !baseEqual;
  }

  public formIsValid(): boolean {
    const isValid = this.frmGroup.controls['noControl'].valid &&
      this.frmGroup.controls['photoUrlControl'].valid &&
      this.frmGroup.controls['categoryControl'].valid &&
      this.frmGroup.controls['tenantControl'].valid &&
      (this.isNewCategory ? this.frmGroup.controls['newCategoryControl'].valid : true)
    return isValid;
  }

  //#region Dropdown compare

  public categoryComparer(t1: Category, t2: Category): boolean {
    return t1 && t2 ? (t1.id === t2.id || t1.name === t2.name) : t1 === t2;
  }

  public tenantComparer(t1: Tenant, t2: Tenant): boolean {
    return t1 && t2 ? (t1.id === t2.id || t1.name === t2.name) : t1 === t2;
  }

  // #endregion

  private sanitizeService(die: Die): Die {
    if (this.isNewCategory) {
      let category = new Category();
      category.tenant = null;
      category.name = this.frmGroup.controls['newCategoryControl'].value;
      category.tenantId = this.frmGroup.controls['tenantControl'].value.id;
      die.category = category;
    }
    else {
      die.category = null;
      die.categoryId = this.frmGroup.controls['categoryControl'].value.id;
    }
    die.tenant = null;
    die.tenantId = this.frmGroup.controls['tenantControl'].value.id;

    die.client = null;

    // Copy FormArray values into the die model
    const detailsRaw = this.dieDetailsArray.getRawValue();
    die.dieDetails = detailsRaw.map(detail => ({
      ...detail,
      a: parseFloat(detail.a),
      b: parseFloat(detail.b),
      t: detail.t !== null && detail.t !== '' ? parseFloat(detail.t) : null,
      weight: detail.weight?.toString() ?? '',
    }));

    // Send base64 only if new photo selected
    if (die.photoBase64 && die.photoBase64.startsWith('data:')) {
      // New image selected
      die.photoUrl = ''; // Optional: clean up if backend reads only base64
    } else {
      // No new image, keep the URL
      die.photoBase64 = ''; // Clear any residual base64 data
    }

    return die;
  }

  private selectDefaultTenant(tenantId: number, tenants?: Tenant[]): void {
    const defaultTenant = tenants?.find((tenant: Tenant) => tenant.id === tenantId);
    if (defaultTenant) {
      this.frmGroup.controls['tenantControl'].setValue(defaultTenant);
      this.getCategoryByTenantId(defaultTenant.id); // Load categories for the selected tenant
    }
  }

  private getDropdownData() {
    this.loadingService.showLoading();

    this.tenant$ = this.tenantService.GetTenantByClientIdAsync().pipe(
      map((result: any) => {
        this.loadingService.hideLoading();

        const tenants = result.body;
        // Automatically select the logged-in user's tenantId in add mode
        if (this.currentDieId === 0) {
          const currentUser = this.authService.getCurrentUser();
          if (currentUser) {
            const loggedInTenantId = currentUser.tenantId;
            this.selectDefaultTenant(loggedInTenantId, tenants);
          }
        }

        return tenants;
      }),
      catchError((error) => {
        this.loadingService.hideLoading();
        this.toasterService.error("Tenant data could not be loaded.");
        return of([])
      })
    );

    this.getDieById(this.currentDieId);
  }

  public getDieById(dieId: number) {
    if (dieId) {
      this.loadingService.showLoading();

      this.dieService.getById(dieId).pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          if (error && error.error) {
            if (error.error.isCustom) {
              this.toasterService.warning(error.error.message);
            } else {
              this.toasterService.error('Something went wrong!');
            }
          } else {
            this.toasterService.error('Something went wrong!');
          }
          return of(null);
        }),
        finalize(() => {
          this.loadingService.hideLoading();
        }),
      ).subscribe(result => {
        if (result) {
          this.die = ObjectUtility.deepClone(result);
          this.dieOriginal = ObjectUtility.deepClone(result);

          this.GetAllDropDownData(this.die);

          this.setForm(this.die);
        }
        this.loadingService.hideLoading();
      });
    }
  }

  public GetAllDropDownData(die: Die) {
    this.getCategoryByTenantId(die.tenantId);
  }

  public getCategoryByTenantId(tenantId: number | undefined): void {
    if (tenantId && tenantId > 0) {
      this.loadingService.showLoading();

      this.categoryService.GetCategoryByTenantIdAsync(tenantId)
        .pipe(
          takeUntil(this.destroy$),
          tap((response: any) => {
            this.category = response.body ?? [];

            // Automatically select the first category if available
            const selectedCategory = this.category.find(c => c.id === this.die.categoryId) || this.category[0] || null;
            this.frmGroup.patchValue({ categoryControl: selectedCategory });
            this.die.category = selectedCategory;

            // Trigger change detection to update the UI
            this.cdr.detectChanges();
          }),
          catchError((error) => {
            this.toasterService.error('Category data could not be loaded.');
            this.category = [];
            this.frmGroup.patchValue({ categoryControl: null });
            return of(null); // Return a safe fallback to keep the stream alive
          }),
          finalize(() => {
            this.loadingService.hideLoading();
          })
        )
        .subscribe(); // Minimal `subscribe()` required to trigger execution
    } else {
      this.category = [];
      this.frmGroup.patchValue({ categoryControl: null });
      this.cdr.detectChanges(); // Trigger change detection for empty state

    }
  }

  public onTenantChange(value: Tenant) {
    if (value) {
      this.getCategoryByTenantId(value.id);
    }
  }

  onPhotoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    if (file) {
      const reader = new FileReader();

      reader.onload = () => {
        const base64 = reader.result as string;
        this.die.photoBase64 = base64;
        this.die.photoUrl = ''; // This ensures old URL is not mistakenly reused
        //this.frmGroup.controls['photoUrlControl'].setValue('selected'); // Any non-null marker

        // Update form control (you can create a separate control or reuse photoUrlControl)
        this.frmGroup.controls['photoUrlControl'].clearValidators(); // Clear validators to allow base64
        this.frmGroup.controls['photoUrlControl'].updateValueAndValidity();
      };

      reader.readAsDataURL(file); // Read file as base64
    }
  }

  removeImage(): void {
    this.die.photoBase64 = '';
    this.die.photoUrl = '';
    this.frmGroup.controls['photoUrlControl'].setValue('');

    // Reset the file input so the same image can be reselected
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  public getImageUrl(path: string): string {
    return UrlHelper.getImageUrl(path);
  }


  public openPdfToImagePopup(): void {
    this.toggleClassPdfToImage();
  }

  public toggleClassPdfToImage(): void {
    this.classAppliedPdfToImage = !this.classAppliedPdfToImage;
  }

  public classAppliedPdfToImageEvent(flag: boolean): void {
    this.classAppliedPdfToImage = flag;
  }

  public saveConfirmationPdfToImageEvent(croppedImage: string): void {
    // Set the cropped image to the photo preview and form control
    //this.photoPreview = croppedImage;

    // Reset the file input so the same image can be reselected
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
    this.frmGroup.controls['photoUrlControl'].clearValidators(); // Clear validators to allow base64
    this.frmGroup.controls['photoUrlControl'].updateValueAndValidity();

    this.die.photoBase64 = croppedImage; // Update the die model with the new base64 image
  }

  toggleNewCategory(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.isNewCategory = input.checked;

    if (this.isNewCategory) {
      // Checkbox ticked then use textbox
      this.frmGroup.get('categoryControl')?.reset();
      this.frmGroup.get('categoryControl')?.clearValidators();
      this.frmGroup.get('newCategoryControl')?.setValidators([Validators.required]);
    } else {
      // Checkbox unticked then use dropdown
      this.frmGroup.get('newCategoryControl')?.reset();
      this.frmGroup.get('newCategoryControl')?.clearValidators();
      this.frmGroup.get('categoryControl')?.setValidators([Validators.required]);
    }

    this.frmGroup.get('categoryControl')?.updateValueAndValidity();
    this.frmGroup.get('newCategoryControl')?.updateValueAndValidity();
  }

}